import { createClient } from '@supabase/supabase-js';

// Environment variables with safe fallbacks
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

console.log('🔍 Supabase configuration check:');
console.log('  - URL exists:', !!supabaseUrl);
console.log('  - Key exists:', !!supabaseAnonKey);
console.log('  - URL valid format:', supabaseUrl ? supabaseUrl.includes('supabase.co') : false);

// Log masked versions for debugging
if (supabaseUrl) {
  console.log('🔍 Supabase URL (masked):', supabaseUrl.substring(0, 20) + '***');
} else {
  console.error('❌ VITE_SUPABASE_URL is not defined in environment variables');
}
if (supabaseAnonKey) {
  console.log('🔍 Supabase Key (masked):', supabaseAnonKey.substring(0, 10) + '***');
} else {
  console.error('❌ VITE_SUPABASE_ANON_KEY is not defined in environment variables');
}

// Declare supabase variable at top level
let supabase: any;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Supabase not configured properly - missing environment variables');
  console.error('❌ Please check your .env file and ensure it contains:');
  console.error('   VITE_SUPABASE_URL=https://your-project.supabase.co');
  console.error('   VITE_SUPABASE_ANON_KEY=your-anon-key');
  
  // Create a mock client for demo purposes
  const mockSupabase = {
    from: () => ({
      select: () => Promise.resolve({ data: [], error: null }),
      insert: () => ({ data: null, error: null }),
      update: () => ({ data: null, error: null }),
      delete: () => ({ error: null }),
      eq: function() { return this; },
      single: function() { return this; },
      order: function() { return this; }
    }),
    auth: {
      signInWithPassword: () => ({ data: null, error: new Error('Demo mode') }),
      signUp: () => ({ data: null, error: new Error('Demo mode') }),
      signOut: () => ({ error: null }),
      getUser: () => ({ data: { user: null } }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
      getSession: () => ({ data: { session: null } })
    }
  };
  supabase = mockSupabase;
} else {
  // Validate URL format
  if (!supabaseUrl.startsWith('https://') || !supabaseUrl.includes('supabase.co')) {
    console.error('❌ Invalid Supabase URL format. Expected: https://your-project.supabase.co');
  }
  
  // Validate key format (basic check)
  if (supabaseAnonKey.length < 100) {
    console.warn('⚠️ Supabase anon key seems too short. Please verify it\'s correct.');
  }
  
  console.log('✅ Supabase client initialized successfully');
  supabase = createClient(supabaseUrl, supabaseAnonKey);
}

// Product operations
let productsCache: any[] | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 60000; // 60 seconds

export const getProducts = async () => {
  const now = Date.now();
  
  try {
    // Check if Supabase is properly configured
    if (!supabaseUrl || !supabaseAnonKey) {
      console.warn('⚠️ Supabase not configured - returning empty array');
      return [];
    }
    
    // Verificar cache
    if (productsCache && (now - lastFetchTime) < CACHE_DURATION) {
      console.log('📦 Using cached products');
      return productsCache;
    }
    
    console.log('🔍 Fetching products from database...');

    // Test connection first
    try {
      const connectionTest = await supabase
        .from('products')
        .select('count', { count: 'exact', head: true });
      
      if (connectionTest.error) {
        console.error('❌ Connection test failed:', connectionTest.error);
        throw new Error(`Connection failed: ${connectionTest.error.message}`);
      }
      console.log('✅ Connection test successful');
    } catch (connectionError) {
      console.error('❌ Failed to connect to Supabase:', connectionError);
      if (connectionError.message?.includes('Failed to fetch')) {
        throw new Error('Cannot connect to Supabase. Please check:\n1. Your internet connection\n2. Supabase URL and key are correct\n3. Supabase project is active');
      }
      throw connectionError;
    }

    // Crear una promesa con timeout
    const fetchWithTimeout = Promise.race([
      // La consulta real
      supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false }),
      
      // Timeout de 30 segundos
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Query timeout - Supabase took too long to respond')), 30000)
      )
    ]);

    console.log('🔍 Attempting products query...');
    const queryStartTime = Date.now();
    const result = await fetchWithTimeout;
    const { data, error } = result as any;
    const queryDuration = Date.now() - queryStartTime;
    console.log(`⏱️ Query completed in ${queryDuration}ms`);
    
    if (error) {
      console.error('❌ Query error:', error);
      console.error('❌ Query error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint
      });
      
      // Provide specific error messages
      if (error.code === 'PGRST301') {
        throw new Error('Database access denied. Please check your Supabase policies.');
      } else if (error.message?.includes('JWT')) {
        throw new Error('Authentication error. Please check your Supabase anon key.');
      }
      
      throw error;
    }

    console.log('✅ Query successful:', data?.length || 0, 'products');
    
    // Actualizar cache
    productsCache = data;
    lastFetchTime = now;
    
    console.log('✅ Products fetched successfully:', data?.length || 0, 'items');
    return data;
  } catch (error) {
    console.error('❌ Error in getProducts:', error);
    
    // More specific error handling
    if (error instanceof Error) {
      console.error('❌ Error message:', error.message);
      
      // Handle specific error types
      if (error.message.includes('Failed to fetch')) {
        console.error('❌ Network Error - Possible causes:');
        console.error('   1. No internet connection');
        console.error('   2. Incorrect Supabase URL');
        console.error('   3. Supabase project is paused/inactive');
        console.error('   4. Firewall blocking the request');
        console.error('   5. CORS issues');
      } else if (error.message.includes('timeout')) {
        console.error('❌ Timeout Error - Supabase is taking too long to respond');
      }
    } else {
      console.error('❌ Unknown error type:', typeof error);
      console.error('❌ Error object:', error);
    }
    
    console.log('🔄 Returning empty array due to error');
    return [];
  }
};

export const getProduct = async (id: string) => {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

export const createProduct = async (product: Omit<any, 'id' | 'created_at' | 'updated_at'>) => {
  const { data, error } = await supabase
    .from('products')
    .insert([product])
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const updateProduct = async (id: string, product: Partial<any>) => {
  const { data, error } = await supabase
    .from('products')
    .update(product)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

export const deleteProduct = async (id: string) => {
  try {
    console.log('🔍 Attempting to delete product with ID:', id);
    
    // Check if we're in demo mode
    if (!supabaseUrl || !supabaseAnonKey) {
      console.warn('⚠️ Demo mode: Simulating product deletion');
      // In demo mode, just simulate success
      return Promise.resolve();
    }
    
    // Verify the product exists before attempting deletion
    const { data: existingProduct, error: fetchError } = await supabase
      .from('products')
      .select('id, name')
      .eq('id', id);
    
    if (fetchError) {
      console.error('❌ Error fetching product before deletion:', fetchError);
      throw new Error(`No se pudo verificar el producto: ${fetchError.message}`);
    }
    
    if (!existingProduct || existingProduct.length === 0) {
      throw new Error('El producto no existe');
    }
    
    console.log('✅ Product found:', existingProduct[0].name);
    
    // Perform deletion with public access (admin security handled in frontend)
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', id);
  
    if (error) {
      console.error('❌ Supabase delete error:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error details:', error.details);
      console.error('❌ Error hint:', error.hint);
      throw error;
    }
    
    console.log('✅ Product deleted successfully');
  } catch (error) {
    console.error('❌ Error in deleteProduct:', error);
    
    // Provide more specific error information
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      throw new Error('Error de conexión: No se pudo conectar con la base de datos. Verifica tu conexión a internet, la configuración de Supabase y que el proyecto esté activo.');
    } else if (error.code === 'PGRST301') {
      throw new Error('Error de permisos: Problema con las políticas RLS. Verifica que las políticas públicas estén configuradas correctamente.');
    } else if (error.code === 'PGRST204') {
      throw new Error('Error de políticas: Las políticas RLS están bloqueando la operación. Ejecuta la migración para corregir las políticas.');
    }
    
    throw error;
  }
};

// Order operations
export const createOrder = async (orderData: {
  customer_email: string;
  customer_name: string;
  customer_address: string;
  customer_city: string;
  customer_postal_code: string;
  shipping_method: string;
  payment_method: string;
  payment_status: string;
  payment_id?: string;
  items: any[];
  subtotal: number;
  shipping_cost: number;
  total: number;
}) => {
  console.log('🔍 Creating order with data:', {
    customer_email: orderData.customer_email,
    payment_method: orderData.payment_method,
    payment_status: orderData.payment_status,
    total: orderData.total
  });

  const { data, error } = await supabase
    .from('orders')
    .insert([orderData])
    .select()
    .single();

  if (error) {
    console.error('❌ Error creating order:', error);
    throw error;
  }

  console.log('✅ Order created successfully with ID:', data.id);
  return data;
};

export const getOrders = async () => {
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data;
};

export const updateOrderStatus = async (id: string, status: string) => {
  const { data, error } = await supabase
    .from('orders')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const confirmOrder = async (orderId: string) => {
  const { data: order, error: orderError } = await supabase
    .from('orders')
    .select('*')
    .eq('id', orderId)
    .single();

  if (orderError) throw orderError;
  if (!order) throw new Error('Order not found');
  if (order.status === 'confirmed') throw new Error('Order already confirmed');

  const items = order.items;

  for (const item of items) {
    const productId = item.product?.id || item.id;
    const quantity = item.quantity;

    const { data: product, error: productError } = await supabase
      .from('products')
      .select('stock')
      .eq('id', productId)
      .single();

    if (productError) throw productError;
    if (!product) throw new Error(`Product ${productId} not found`);

    const newStock = (product.stock || 0) - quantity;
    if (newStock < 0) {
      throw new Error(`Stock insuficiente para el producto ${item.product?.name || item.name}`);
    }

    const { error: updateError } = await supabase
      .from('products')
      .update({ stock: newStock })
      .eq('id', productId);

    if (updateError) throw updateError;
  }

  const { data: updatedOrder, error: updateOrderError } = await supabase
    .from('orders')
    .update({ status: 'confirmed', updated_at: new Date().toISOString() })
    .eq('id', orderId)
    .select()
    .single();

  if (updateOrderError) throw updateOrderError;
  return updatedOrder;
};

// Category operations
export const getCategories = async () => {
  try {
    const { data, error } = await supabase
      .from('categories')
      .select('*');

    if (error) throw error;

    // Sort manually to avoid minification issues with chained .order() calls
    const sortedData = (data || []).sort((a, b) => {
      // First sort by parent_id (nulls first)
      if (a.parent_id === null && b.parent_id !== null) return -1;
      if (a.parent_id !== null && b.parent_id === null) return 1;
      if (a.parent_id !== b.parent_id) {
        return (a.parent_id || '').localeCompare(b.parent_id || '');
      }
      // Then sort by name
      return a.name.localeCompare(b.name);
    });

    return sortedData;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};

// Auth operations
export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  
  if (error) throw error;
  return data;
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

// Export the supabase client
export { supabase };